import type { LucideIcon } from 'lucide-react';

export interface SidebarItem {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Route path; absent when the item is a parent that only groups children. */
  path?: string;
  children?: SidebarItem[];
  /** Restrict to specific roles. `undefined` = visible to all authenticated users. */
  roles?: string[];
  /** Shorthand for `roles: ['admin']`. */
  adminOnly?: boolean;
  /** Optional badge text rendered to the right of the label. */
  badge?: string;
}

/** Return true if the item should be shown to a user with the given role. */
export function isSidebarItemVisible(item: SidebarItem, userRole: string): boolean {
  if (item.adminOnly && userRole !== 'admin') return false;
  if (item.roles && !item.roles.includes(userRole)) return false;
  return true;
}

/**
 * Recursively filter a sidebar tree by role.
 * Drops parent items whose children are all filtered out.
 */
export function filterNavigation(items: SidebarItem[], userRole: string): SidebarItem[] {
  return items.reduce<SidebarItem[]>((acc, item) => {
    if (!isSidebarItemVisible(item, userRole)) return acc;
    if (item.children) {
      const visibleChildren = filterNavigation(item.children, userRole);
      if (visibleChildren.length > 0) {
        acc.push({ ...item, children: visibleChildren });
      }
    } else {
      acc.push(item);
    }
    return acc;
  }, []);
}
